/**
 * @shopkit/asset-cache
 *
 * Asset caching system for storefronts with Service Worker support.
 * Provides intelligent caching strategies for images, fonts, and other assets.
 *
 * ## Features
 * - Multiple caching strategies (cache-first, network-first, stale-while-revalidate)
 * - Service Worker integration for offline support
 * - Cache versioning and automatic invalidation
 * - Tag-based cache management
 * - Size-based cache limits
 *
 * ## Usage
 *
 * ```typescript
 * import { initAssetCache, AssetLoader } from '@shopkit/asset-cache';
 *
 * // Initialize the cache system
 * await initAssetCache({
 *   enabled: true,
 *   version: '1.0.0',
 *   storageName: 'my-store-assets',
 *   maxSize: 50, // MB
 *   maxAge: 86400, // 24 hours
 *   serviceWorkerEnabled: true,
 * });
 *
 * // Load assets with caching
 * const imageBlob = await AssetLoader.load('https://example.com/image.jpg');
 * ```
 *
 * @packageDocumentation
 */

// Export types
export * from "./config/types";

// Export constants
export * from "./config/constants";

// Export utilities
export * from "./utils/cache-helpers";
export * from "./utils/hash-generator";

// Export strategies
export * from "./strategies/cache-first";
export * from "./strategies/network-first";
export * from "./strategies/stale-while-revalidate";

// Export main components
export {
  AssetLoader,
  AssetLoaderState,
} from "./core/asset-loader";
export type { AssetLoaderInitResult } from "./core/asset-loader";
export { CacheManager, CacheManagerState } from "./core/cache-manager";
export type { InitResult } from "./core/cache-manager";
export { VersionManager } from "./core/version-manager";
export {
  registerServiceWorker,
  sendMessageToSW,
  isServiceWorkerActive,
  unregisterAllServiceWorkers,
} from "./core/register-sw";

// Export errors
export * from "./core/errors";

// Export initialization
export * from "./init";
